import React, { useEffect, useRef, useState } from 'react';
import axios from '../../utils/axios';
import Sidebar from './sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import resolveImageUrl from '../../utils/imageUtils';

const SHOW_ACTIVITY_HISTORY = false;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
  }

  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      const strVal = String(realValue ?? "");
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const areComparableValuesDifferent = (adminVal, mlVal, field, selectedProperty) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  const a = normalize(adminVal);

  if ((field === "landarea" || field === "coveredarea") && mlVal) {
    const range = parseRange(mlVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value;
        case "less":
          return a >= range.value;
        case "between":
          return a < range.min || a > range.max;
        case "exact":
          return Math.abs(a - range.value) > 1e-9;
      }
    }
  }

  const m = normalize(getMLDisplayValue(selectedProperty, field));
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};









const FlaggedByAlgorithm = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [comment, setComment] = useState('');
  const [agreeWithAi, setAgreeWithAi] = useState(null); // can be true, false, or 'partial'
  const [isEditingResponse, setIsEditingResponse] = useState(false);
  const responseInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [highlightPin, setHighlightPin] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [propertyTimeline, setPropertyTimeline] = useState({ timeline: [] });





  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const getPt5Reference = (property) =>
    property?.refno ?? property?.pt5 ?? property?.pt_5 ?? property?.pt ?? "";

  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const navigate = useNavigate();

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setShowDetailModal(false);
    setRotation(0);
    setIsEditingResponse(false);
    setComment("");
    setAgreeWithAi(null);
    setPropertyTimeline({ timeline: [] });
    highlightPropertyCard(pinToHighlight);
    navigate({ search: "" }, { replace: true });
  };

  const user = getUser();
  const userId = user?.id;

  const fetchPropertyTimeline = async (pin) => {
    try {
      const res = await axios.get(`/properties/timeline/${pin}`);
      setPropertyTimeline(res.data);
    } catch (err) {
      console.error("Failed to fetch property timeline:", err);
      setPropertyTimeline({ timeline: [] });
    }
  };

  const fetchFlaggedProperties = async () => {
    try {
      setLoading(true);
      // const res = await axios.get(`/inspector/algorithm-flagged-filtered/${userId}`);
      // setProperties(dedupeByPin(res.data || []));
      const res = await axios.get(`/inspector/algorithm-flagged-filtered/${userId}`);
      const fetchBust = Date.now();
      setProperties(dedupeByPin(res.data || []).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
    } catch (err) {
      console.error('Error fetching algorithm-flagged properties:', err);
      toast.error('Failed to load flagged properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchFlaggedProperties();
    } else {
      setProperties([]);
      setLoading(false);
    }
  }, [userId]);

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.property_id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      if (selectedImage && selectedProperty.image && selectedImage.includes(resolveImageUrl(selectedProperty.image))) {
        setSelectedImage(appendCacheBuster(resolveImageUrl(selectedProperty.image), bust));
      }
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!comment.trim()) {
      toast.error('Please enter your response');
      return;
    }
    if (agreeWithAi === null) {
      toast.error('Please select Yes, No, or Partially Agree');
      return;
    }

    try {
      setSubmitting(true);

      // Map agreeWithAi to value for backend: true=1, false=0, 'partial'=2
      const agreeValue = agreeWithAi === 'partial' ? 2 : (agreeWithAi ? 1 : 0);

      await axios.post('/inspector/algorithm-flag-response', {
        propertyId: selectedProperty.property_id,
        inspectorId: userId,
        response: comment.trim(),
        agreeWithAi: agreeValue,
      });

      toast.success('Response submitted successfully');
      fetchFlaggedProperties();

      setComment('');
      setAgreeWithAi(null);
      setIsEditingResponse(false);
      setShowDetailModal(false);

    } catch (err) {
      console.error('Error submitting response:', err);
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.9) return 'text-red-600 bg-red-50';
    if (score >= 0.8) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const getFilteredProperties = () => {
    if (filterStatus === 'responded') {
      return properties.filter(p => p.inspector_response && p.rejected_by_supervisor !== 1);
    } else if (filterStatus === 'pending') {
      return properties.filter(p => !p.inspector_response && p.rejected_by_supervisor !== 1);
    } else if (filterStatus === 'rejected') {
      return properties.filter(p => p.rejected_by_supervisor === 1);
    }
    return properties;
  };

  const filteredProperties = getFilteredProperties();
  const respondedCount = (properties || []).filter(p => p.inspector_response && p.rejected_by_supervisor !== 1).length || 0;
  const pendingCount = (properties || []).filter(p => !p.inspector_response && p.rejected_by_supervisor !== 1).length || 0;
  const rejectedCount = (properties || []).filter(p => p.rejected_by_supervisor === 1).length || 0;

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (!properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );

    if (matched) {
      if (filterStatus !== "all") setFilterStatus("all");
      setHighlightPin(normalizePin(matched.pin));
      setSelectedProperty(matched);
      setShowDetailModal(true);
      fetchPropertyTimeline(matched.pin);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin, filterStatus]);

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
  };

  const formatAreaValue = (val) => {
    if (!hasUsableValue(val)) return "N/A";
    const n = toFiniteNumber(val);
    if (n === null) return String(val);
    return String(Math.round(n));
  };

  const formatMLValue = (val) => {
    if (!hasUsableValue(val)) return "N/A";
    const strVal = String(val);
    const isRange = strVal.toLowerCase().includes("greater") ||
      strVal.toLowerCase().includes("less") ||
      strVal.toLowerCase().includes("between");
    if (isRange) {
      return val;
    }
    const num = Number(val);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Math.round(num);
    }
    return val;
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areComparableValuesDifferent = (adminVal, mlVal, field) => {
    const toComparable = (val) => {
      if (val === null || val === undefined) return null;
      let raw = String(val).trim();
      if (!raw) return null;
      raw = raw.replace(/sq\.?\s*(yards?|yds?|ft|feet?)/gi, '').replace(',', '').trim();
      const lowered = raw.toLowerCase();
      if (lowered === 'n/a' || lowered === 'null' || lowered === 'undefined') return null;
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    if ((field === "landarea" || field === "coveredarea") && mlVal) {
      const range = parseRange(formatMLValue(mlVal));
      const a = toComparable(adminVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return !(a > range.value);
          case "less":
            return !(a < range.value);
          case "between":
            const inRange = a >= range.min && a <= range.max;
            return !inRange;
          case "exact":
            const exactMatch = Math.abs(a - range.value) <= 1e-9;
            return !exactMatch;
        }
      }
    }

    const a = toComparable(adminVal);
    const m = toComparable(formatMLValue(mlVal));
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const handleImageClick = (e, imageUrl, property) => {
    e.stopPropagation();
    setSelectedImage(appendCacheBuster(resolveImageUrl(imageUrl), property?._imgBust));
    setShowImageModal(true);
  };

  const handleEditPreviousResponse = () => {
    setIsEditingResponse(true);
    setComment(selectedProperty?.inspector_response || '');
    if (selectedProperty?.agree_with_ai === 0) {
      setAgreeWithAi(false);
    } else if (selectedProperty?.agree_with_ai === 1) {
      setAgreeWithAi(true);
    } else if (selectedProperty?.agree_with_ai === 2) {
      setAgreeWithAi('partial');
    } else {
      setAgreeWithAi(null);
    }
    setTimeout(() => responseInputRef.current?.focus(), 0);
  };

  const getMLDisplayValue = (property, field) => {
    const pick = (keys) => {
      for (const k of keys) {
        const v = property?.[k];
        if (hasUsableValue(v)) return v;
      }
      return null;
    };

    switch (field) {
      case 'floor':
        return pick(["floors_ml", "floor_ml", "floors_pred", "floor_pred"]) ?? 'N/A';
      case 'landuse':
        return pick(["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]) ?? 'N/A';
      case 'landarea': {
        const ml = pick(["landarea_ml", "land_area_ml"]);
        const bucket = pick(["landarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        return (hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish))
          ? bucket
          : (hasUsableValue(ml) ? ml : (bucket ?? 'N/A'));
      }
      case 'coveredarea': {
        const ml = pick(["coveredarea_ml", "covered_area_ml"]);
        const bucket = pick(["coveredarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        return (hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish))
          ? bucket
          : (hasUsableValue(ml) ? ml : (bucket ?? 'N/A'));
      }
      default:
        return 'N/A';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-4 md:p-6 overflow-y-auto">
        <Toaster position="top-right" />

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Flagged by Computer Vision
              </h1>
              <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
                Hello!<br />
                These properties have been flagged by the Computer Vision algorithm and require your review. Please go through each property and decide whether you want to reassess each or not.<br />
                Once you review these properties, they will be sent to your supervisor.
                {/* Properties that are rejected by your supervisor can be seen in the 'Rejected by Supervisor' tab below. */}
              </p>
            </div>
            {!loading && (
              <div className="text-xs sm:text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
                Showing <span className="font-semibold">{filteredProperties.length}</span> of{" "}
                <span className="font-semibold">{properties.length}</span> properties
              </div>
            )}
          </div>
          {/* <button
            onClick={fetchFlaggedProperties}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center"
            data-click-id="flagged_by_AI_refresh"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button> */}
        </div>

        {!loading && properties.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'all'
                  ? 'bg-white text-purple-600 shadow'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
                data-click-id="flagged_by_Supervisor_all"
              >
                All ({(properties || []).length || 0})
              </button>
              <button
                onClick={() => setFilterStatus('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'pending'
                  ? 'bg-white text-purple-600 shadow'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
                data-click-id="flagged_by_Supervisor_pending"
              >
                Pending ({pendingCount})
              </button>
              <button
                onClick={() => setFilterStatus('responded')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'responded'
                  ? 'bg-white text-purple-600 shadow'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
                data-click-id="flagged_by_Supervisor_responded"
              >
                Responded ({respondedCount})
              </button>
              {/*
              <button
                onClick={() => setFilterStatus('rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'rejected'
                  ? 'bg-white text-purple-600 shadow'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
                data-click-id="flagged_by_Supervisor_rejected"
              >
                Rejected by Supervisor ({rejectedCount})
              </button>
              */}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading flagged properties...</p>
            </div>
          </div>
        )}

        {!loading && filteredProperties.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <div className="text-6xl mb-6">🤖</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {filterStatus === 'responded'
                ? 'No Responded Properties'
                : filterStatus === 'pending'
                  ? 'No Pending Properties'
                  : filterStatus === 'rejected'
                    ? 'No Rejected Properties'
                    : 'No Algorithm-Flagged Properties'}
            </h3>
            <p className="text-gray-600">
              {filterStatus === 'responded'
                ? 'You haven\'t responded to any flagged properties yet.'
                : filterStatus === 'pending'
                  ? 'All flagged properties have been responded to.'
                  : filterStatus === 'rejected'
                    ? 'No properties have been rejected by the supervisor yet.'
                    : 'There are no properties flagged by the algorithm at this time.'}
            </p>
          </div>
        )}

        {!loading && filteredProperties.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map((property) => {
              const isSelected = normalizePin(property.pin) && normalizePin(property.pin) === highlightPin;

              return (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-pin={normalizePin(property.pin)}
                  className={`bg-white shadow hover:shadow-lg cursor-pointer transition-all relative rounded-xl ${isSelected ? "border-2 border-sky-500" : "border border-gray-200"
                    }`}
                  onClick={() => {
                    setSelectedProperty(property);
                    setIsEditingResponse(false);
                    setShowDetailModal(true);
                    setComment('');
                    setRotation(0);
                    setAgreeWithAi(null);
                    fetchPropertyTimeline(property.pin);
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  <div className="h-48 bg-gray-100 overflow-hidden relative group rounded-t-xl">
                    {property.image ? (
                      <>
                        <img
                          src={appendCacheBuster(resolveImageUrl(property.image), property._imgBust)}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                          alt="Property"
                        />
                        <div
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          onClick={(e) => handleImageClick(e, property.image, property)}
                        >
                          <div className="bg-white/90 p-3 rounded-full">
                            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
                        <span className="text-4xl">🏢</span>
                      </div>
                    )}

                    {property.confidence_score && Math.round(property.confidence_score * 100) < 100 && (
                      <div className="absolute top-3 right-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(property.confidence_score)}`}>
                          {Math.round(property.confidence_score * 100)}% Match
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      PIN: {property.pin}
                    </h3>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div>Circle: {property.circle || 'N/A'}</div>

                      {/*  <div className="grid grid-cols-1 gap-1 mt-3 py-2 border-y border-gray-100 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Floor (Admin/Computer Vision):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.floor_admin) ? property.floor_admin : "N/A"} /{" "}
                            {formatMLValue(getMLDisplayValue(property, "floor"))}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Use (Admin/Computer Vision):</span>
                          <span className="font-medium truncate ml-2 text-right max-w-[120px]">
                            {hasUsableValue(property.landuse_admin) ? property.landuse_admin : "N/A"} /{" "}
                            {formatMLValue(getMLDisplayValue(property, "landuse"))}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Area (Admin/Computer Vision):</span>
                          <span className="font-medium">{formatAreaValue(property.landarea_admin)} / {formatAreaValue(getMLDisplayValue(property, 'landarea'))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Covered Area (Admin/Computer Vision):</span>
                          <span className="font-medium">{formatAreaValue(property.coveredarea_admin)} / {formatAreaValue(getMLDisplayValue(property, 'coveredarea'))}</span>
                        </div>
                      </div> */}

                      {property.rejected_by_supervisor === 1 && (
                        <div className="mt-2 p-2 bg-red-50 rounded-lg">
                          <span className="text-red-700 text-xs font-semibold">
                            ⚠️ Rejected by Supervisor
                          </span>
                        </div>
                      )}

                      {property.inspector_response && (
                        <div className="mt-2 p-2 bg-green-50 rounded-lg">
                          <span className="text-green-700 text-xs font-semibold">
                            ✓ Responded on {new Date(property.response_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                      <button
                        className="flex-1 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors"
                        data-click-id="flagged_by_Supervisor_view_details"
                        data-property-id={property.pin}
                      >
                        {property.inspector_response ? 'View Response →' : 'View & Respond →'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full h-full shadow-2xl p-6 relative overflow-y-auto"
              >
                <button
                  onClick={closeDetailModal}
                  className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
                  data-click-id="flagged_by_Supervisor_close"
                  data-property-id={selectedProperty.pin}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="mb-6 pr-14">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500">
                    <p>
                      PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span>
                    </p>
                    {/*  <p>
                      PT-5 Reference:{" "}
                      <span className="font-semibold text-gray-700">
                        {getPt5Reference(selectedProperty) || "N/A"}
                      </span>
                    </p> */}
                    {selectedProperty.flagged_date && (
                      <p>
                        Flagged:{" "}
                        <span className="font-semibold text-gray-700">
                          {new Date(selectedProperty.flagged_date).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="relative group bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer h-80">
                    {selectedProperty.image ? (
                      <>
                        <div
                          className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                          style={{ transform: `rotate(${rotation}deg)` }}
                          onClick={() => {
                            setSelectedImage(appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust));
                            setShowImageModal(true);
                          }}
                        >
                          <img
                            src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                            className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                            alt="Property"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={handleRotate}
                            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          {rotation !== 0 && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-60 bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                        No Image
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border rounded-lg overflow-hidden text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-2 text-left font-medium">Feature</th>
                            <th className="p-2 text-left font-medium">Admin</th>
                            <th className="p-2 text-left font-medium">Computer Vision</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: 'Floor', admin: selectedProperty.floor_admin, ml: getMLDisplayValue(selectedProperty, 'floor'), field: 'floor' },
                            { label: 'Land Use', admin: selectedProperty.landuse_admin, ml: getMLDisplayValue(selectedProperty, 'landuse'), field: 'landuse' },
                            { label: 'Land Area', admin: selectedProperty.landarea_admin, ml: getMLDisplayValue(selectedProperty, 'landarea'), field: 'landarea' },
                            { label: 'Covered Area', admin: selectedProperty.coveredarea_admin, ml: getMLDisplayValue(selectedProperty, 'coveredarea'), field: 'coveredarea' },
                          ].map((row) => {
                            const isArea = row.label === 'Land Area' || row.label === 'Covered Area';
                            const isDifferent = areComparableValuesDifferent(row.admin, row.ml, row.field);
                            return (
                              <tr key={row.label} className={`border-b ${isDifferent ? 'bg-red-100' : ''}`}>
                                <td className="p-2 font-medium">{row.label}</td>
                                <td className="p-2">
                                  {isArea
                                    ? (() => {
                                      const v = formatAreaValue(row.admin);
                                      if (v === "N/A") return "N/A";
                                      return `${v} ${row.label === "Land Area" ? "sq.yards" : "sq.ft"}`;
                                    })()
                                    : (row.admin === null || row.admin === undefined || String(row.admin).trim() === '' ? 'N/A' : row.admin)}
                                </td>
                                <td className="p-2">
                                  {/* {isArea ? formatAreaValue(row.ml) : formatMLValue(row.ml)} */}
                                  {isArea ? '' : formatMLValue(row.ml)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {selectedProperty.rejected_by_supervisor === 1 && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="font-semibold text-red-800 mb-2">
                      ⚠️ Rejected by Supervisor
                    </h3>
                    {selectedProperty.rejected_by_supervisor_at && (
                      <p className="text-red-700 text-sm mb-2">
                        Rejected on {new Date(selectedProperty.rejected_by_supervisor_at).toLocaleDateString()}
                      </p>
                    )}
                    {selectedProperty.rejection_comment && (
                      <p className="text-red-700 whitespace-pre-wrap mb-2">
                        <span className="font-semibold">Supervisor's comment: </span>
                        {selectedProperty.rejection_comment}
                      </p>
                    )}
                    <p className="text-red-700">
                      Please review and update your response to this property.
                    </p>
                  </div>
                )}

                {selectedProperty.inspector_response && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-green-800">
                        ✓ Previous Response ({new Date(selectedProperty.response_date).toLocaleDateString()})
                      </h3>
                      <button
                        type="button"
                        onClick={handleEditPreviousResponse}
                        className="px-3 py-1 rounded-full text-xs font-semibold bg-white text-green-800 border border-green-200 hover:bg-green-100 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                    {(selectedProperty.agree_with_ai === 0 || selectedProperty.agree_with_ai === 1 || selectedProperty.agree_with_ai === 2) && (
                      <p className="text-green-800 text-sm mb-2">
                        Do you agree with the Computer Vision prediction? <strong>
                          {selectedProperty.agree_with_ai === 1 ? 'Yes' :
                            selectedProperty.agree_with_ai === 2 ? 'Partially Agree' : 'No'}
                        </strong>
                      </p>
                    )}
                    <p className="text-green-700">{selectedProperty.inspector_response}</p>
                  </div>
                )}

                {SHOW_ACTIVITY_HISTORY && propertyTimeline.timeline && propertyTimeline.timeline.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Activity History</h3>
                    <div className="space-y-3">
                      {propertyTimeline.timeline
                        .filter(
                          (item) =>
                            !(
                              item.type === "algorithm_flag" &&
                              (
                                item.text === "Flagged by Computer Vision" ||
                                item.text === "Flagged by Admin via Bulk Assignment"
                              )
                            )
                        )
                        .map((item) => {
                          const isInspector = item.user_role === "inspector";
                          const isSupervisor = item.user_role === "supervisor";

                          return (
                            <div key={item.id} className="flex gap-3">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isInspector ? "bg-blue-500" : isSupervisor ? "bg-green-500" : "bg-gray-500"
                                }`}>
                                {item.user_name ? item.user_name.charAt(0).toUpperCase() : "?"}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-800">
                                    {item.user_name || "Unknown User"}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(item.created_at).toLocaleString()}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                    {item.stage}
                                  </span>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  {item.type === "comment" && <p className="text-sm text-gray-700">{item.text}</p>}
                                  {item.type === "supervisor" && <p className="text-sm text-gray-700">{item.text}</p>}
                                  {item.type === "inspector_explanation" && (
                                    <div className="text-sm text-gray-700">
                                      <p className="font-medium mb-2">Overall: {item.overall}</p>
                                      {item.rows.floor && <p>Floor: {item.rows.floor}</p>}
                                      {item.rows.landuse && <p>Land Use: {item.rows.landuse}</p>}
                                      {item.rows.landarea && <p>Land Area: {item.rows.landarea}</p>}
                                      {item.rows.coveredarea && <p>Covered Area: {item.rows.coveredarea}</p>}
                                    </div>
                                  )}
                                  {item.type === "action" && (
                                    <p className="text-sm text-gray-700">
                                      {item.action.replace(/_/g, " ")}
                                      {item.details && (
                                        <span className="ml-2 text-gray-500">- {JSON.stringify(item.details)}</span>
                                      )}
                                    </p>
                                  )}
                                  {item.type === "algorithm_flag" && <p className="text-sm text-gray-700">{item.text}</p>}
                                  {item.type === "algorithm_response" && <p className="text-sm text-gray-700">{item.text}</p>}
                                  {item.type === "algorithm_rejected" && <p className="text-sm text-gray-700">{item.text}</p>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {(!selectedProperty.inspector_response || isEditingResponse) ? (
                  <>
                    <div className="mb-4">
                      <h3 className="text-md font-semibold text-gray-800 mb-3">
                        Do you agree with the Computer Vision prediction for this property?
                      </h3>
                      <div className="flex gap-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setAgreeWithAi(true)}
                          disabled={submitting}
                          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${agreeWithAi === true
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setAgreeWithAi('partial')}
                          disabled={submitting}
                          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${agreeWithAi === 'partial'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          Partially Agree
                        </button>
                        <button
                          type="button"
                          onClick={() => setAgreeWithAi(false)}
                          disabled={submitting}
                          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${agreeWithAi === false
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          No
                        </button>
                      </div>

                      {/* <h3 className="text-md font-semibold text-gray-800 mb-2">Inspector Response</h3>
                      <textarea
                        ref={responseInputRef}
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Enter your response to the algorithm flag. Provide your assessment, observations, or any additional information..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                        disabled={submitting}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Your response will be recorded and sent to the supervisor.
                      </p>
                    </div>

                    <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSubmitResponse}
                        disabled={!comment.trim() || agreeWithAi === null || submitting}
                        className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors text-sm font-medium ${
                          comment.trim() && agreeWithAi !== null && !submitting
                            ? 'bg-purple-600 hover:bg-purple-700'
                            : 'bg-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {submitting ? 'Submitting...' : (selectedProperty.inspector_response ? 'Update Response' : 'Submit Response')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedProperty.inspector_response) {
                            setIsEditingResponse(false);
                            setComment('');
                            setAgreeWithAi(null);
                            return;
                          }
                          closeDetailModal();
                        }}
                        className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        {selectedProperty.inspector_response ? 'Cancel Edit' : 'Cancel'}
                      </button>
                    </div> */}
                    </div>
                  </>
                ) : (
                  <></>
                )}

                <div className="flex-shrink-0 flex justify-end gap-4 p-6 pt-4 border-t border-gray-200">
                  {selectedProperty.reassess !== "0" && (
                    <button
                      onClick={() => navigate(`/inspector/do-not-reassess/${selectedProperty.pin}`, {
                        state: { agreeWithAi: agreeWithAi }
                      })}
                      className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium shadow hover:bg-gray-700 transition"
                      data-click-id="flagged-by-ai-do-not-reassess"
                      data-property-id={selectedProperty.pin}
                    >
                      Do Not Reassess
                    </button>
                  )}
                  {selectedProperty.reassess !== "1" && (
                    <button
                      onClick={() => navigate(`/inspector/reassess/${selectedProperty.pin}`, {
                        state: { agreeWithAi: agreeWithAi }
                      })}
                      className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium shadow hover:bg-gray-700 transition"
                      data-click-id="flagged-by-ai-reassess"
                      data-property-id={selectedProperty.pin}
                    >
                      Reassess
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showImageModal && selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
              onClick={() => setShowImageModal(false)}
            >
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                src={selectedImage}
                alt="Full size property"
                className="max-w-[90vw] max-h-[90vh] object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>




      </main>
    </div>
  );
};

export default FlaggedByAlgorithm;
